import React, { useState, useRef, useEffect } from 'react';
import { 
  FiBell, 
  FiX, 
  FiCheck, 
  FiTrash2, 
  FiEye, 
  FiEyeOff,
  FiMail,
  FiUser,
  FiSettings,
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiLock,
  FiUsers,
  FiFolder,
  FiActivity,
  FiStar,
  FiClock,
  FiRefreshCw
} from 'react-icons/fi';
import { useNotifications } from '../contexts/NotificationContext';
import './NotificationDropdown.css';

const NotificationDropdown = ({ isOpen, onClose, triggerRef }) => {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications
  } = useNotifications();

  const dropdownRef = useRef(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [processingNotifications, setProcessingNotifications] = useState(new Set());

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleMarkAsRead = async (notificationId, event) => {
    event.stopPropagation();
    setProcessingNotifications(prev => new Set(prev).add(notificationId));
    try {
      await markAsRead(notificationId);
    } finally {
      setProcessingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleDelete = async (notificationId, event) => {
    event.stopPropagation();
    setProcessingNotifications(prev => new Set(prev).add(notificationId));
    try {
      await deleteNotification(notificationId);
    } finally {
      setProcessingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshNotifications();
    setIsRefreshing(false);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    if (!type) return <FiBell />;
    
    const lowerType = type.toLowerCase();
    
    switch (lowerType) {
      case 'email':
      case 'email_notification':
        return <FiMail />;
      case 'campaign':
      case 'campaign_notification':
        return <FiBell />;
      case 'template':
      case 'template_notification':
        return <FiCheck />;
      case 'user':
      case 'user_notification':
      case 'user_login':
      case 'superadmin_login':
        return <FiUser />;
      case 'system':
      case 'system_notification':
        return <FiSettings />;
      case 'warning':
      case 'warning_notification':
        return <FiAlertTriangle />;
      case 'error':
      case 'error_notification':
        return <FiAlertTriangle />;
      case 'success':
      case 'success_notification':
        return <FiCheckCircle />;
      case 'info':
      case 'info_notification':
        return <FiInfo />;
      case 'login':
      case 'logout':
        return <FiLock />;
      case 'invitation':
      case 'invite':
        return <FiMail />;
      case 'team':
      case 'team_notification':
        return <FiUsers />;
      case 'project':
      case 'project_notification':
        return <FiFolder />;
      case 'activity':
      case 'activity_notification':
        return <FiActivity />;
      default:
        return <FiBell style={{color: '#6b7280'}} />;
    }
  };

  const getNotificationClass = (notification) => {
    if (!notification.IsView) return 'notification-item unread';
    return 'notification-item';
  };

  const getNotificationPriority = (notification) => {
    const priority = notification.Priority || 'normal';
    switch (priority.toLowerCase()) {
      case 'high':
        return 'priority-high';
      case 'urgent':
        return 'priority-urgent';
      default:
        return 'priority-normal';
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`notification-dropdown ${isOpen ? 'show' : ''}`} 
      ref={dropdownRef}
    >
      <div className="notification-header">
        <div className="header-left">
          <h3>Notifications</h3>
          {unreadCount > 0 && (
            <span className="unread-count-badge">{unreadCount}</span>
          )}
        </div>
        <div className="notification-actions">
          <button
            className="refresh-btn"
            onClick={handleRefresh}
            title="Refresh notifications"
            disabled={isRefreshing}
          >
            <span className={isRefreshing ? 'spinning' : ''} style={{fontSize: '16px', fontWeight: 'bold'}}>🔄</span>
          </button>
          {unreadCount > 0 && (
            <button
              className="mark-all-read-btn"
              onClick={handleMarkAllAsRead}
              title="Mark all as read"
            >
              <span>👁️</span>
              <span>Mark all read</span>
            </button>
          )}
          <button
            className="close-btn"
            onClick={onClose}
            title="Close"
          >
            <span>✕</span>
          </button>
        </div>
      </div>

      <div className="notification-content">
        {loading ? (
          <div className="notification-loading">
            <div className="loading-spinner"></div>
            <span>Loading notifications...</span>
          </div>
        ) : error ? (
          <div className="notification-error">
            <FiAlertTriangle className="error-icon" />
            <span>Error loading notifications</span>
            <p>{error}</p>
            <button onClick={handleRefresh} className="retry-btn">
              <FiRefreshCw />
              Retry
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notification-empty">
            <FiBell className="empty-icon" />
            <span>No notifications</span>
            <p>You're all caught up!</p>
            <button onClick={handleRefresh} className="refresh-empty-btn">
              <FiRefreshCw />
              Refresh
            </button>
          </div>
        ) : (
          <div className="notification-list">
            {notifications.map((notification) => (
              <div
                key={notification.NotificationId}
                className={`${getNotificationClass(notification)} ${getNotificationPriority(notification)}`}
                data-type={notification.Type}
                onClick={() => markAsRead(notification.NotificationId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    markAsRead(notification.NotificationId);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`${notification.Title}. ${notification.Message}. Click to mark as read.`}
              >
                <div className="notification-icon">
                  {getNotificationIcon(notification.Type)}
                </div>
                <div className="notification-item-content">
                  <div className="notification-title">
                    {notification.Title}
                  </div>
                                  <div className="notification-message" aria-label={`Notification message: ${notification.Message}`}>
                  {notification.Message}
                </div>
                  <div className="notification-time">
                    <FiClock />
                    {formatTime(notification.createdAt)}
                  </div>
                </div>
                {/* Read status indicator (double tick) at bottom-right */}
                <div
                  className={`notification-read-indicator ${notification.IsView ? 'read' : 'unread'}`}
                  aria-label={notification.IsView ? 'Read' : 'Unread'}
                >
                  <span className="tick tick-1">✓</span>
                  <span className="tick tick-2">✓</span>
                </div>
                <div className="notification-actions">
                                    {!notification.IsView && (
                    <button
                      className="action-btn mark-read-btn"
                      onClick={(e) => handleMarkAsRead(notification.NotificationId, e)}
                      title="Mark as read"
                      aria-label={`Mark notification "${notification.Title}" as read`}
                      disabled={processingNotifications.has(notification.NotificationId)}
                    >
                      {processingNotifications.has(notification.NotificationId) ? (
                        <div className="loading-spinner" style={{width: '16px', height: '16px'}}></div>
                      ) : (
                        <span style={{fontSize: '14px', fontWeight: 'bold'}}>👁</span>
                      )}
                    </button>
                  )}
                  <button
                    className="action-btn delete-btn"
                    onClick={(e) => handleDelete(notification.NotificationId, e)}
                    title="Delete"
                    aria-label={`Delete notification "${notification.Title}"`}
                    disabled={processingNotifications.has(notification.NotificationId)}
                  >
                      {processingNotifications.has(notification.NotificationId) ? (
                        <div className="loading-spinner" style={{width: '16px', height: '16px'}}></div>
                      ) : (
                        <span style={{fontSize: '14px'}}>🗑</span>
                      )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {notifications.length > 0 && (
        <div className="notification-footer">
          <span>{notifications.length} notification{notifications.length !== 1 ? 's' : ''}</span>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount} unread</span>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown; 